import React, { useEffect, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { colors } from '@/constants/colors';
import IspProofUpload from './IspProofUpload';
import IspPortalDiag from './IspPortalDiag';
import IspBillSheet from './IspBillSheet';
import {
   startIspPortalSession,
   getIspPortalMe,
   submitIspPortalProof,
   getIspPublicTenant,
} from '@/api/isp';
import {
   PublicReset,
   BrandHeader,
   BrandFooter,
   PayMethod,
   Shell,
   Card,
   CardTitle,
   CardNote,
   Field,
   Lbl,
   Input,
   Hint,
   Err,
   Primary,
   Fine,
} from './ispPublicUI';

/**
 * The subscriber's own billing page.
 *
 * ─── WHAT THIS PAGE IS FOR ───
 * Somebody whose internet has just stopped, on mobile data, wanting two things:
 * am I cut off and why, and where do I send the money. So the page LEADS with
 * their status and the amount, and the payment QR is the next thing under it.
 * Ray, 2026-09-01: *"the login account is secondary… the main content in the
 * portal UI is you are disconnected or overdue."*
 *
 * ─── SIGNING IN IS A PHONE NUMBER ───
 * No password: a subscriber has no account to have one on, and asking them to
 * make one loses most of them at the first screen. The sign-in card is small
 * and sits under the operator's branding, because it is a doorway, not the
 * destination.
 *
 * ─── UPLOADING A SCREENSHOT IS NOT PAYING ───
 * 🔴 The single most important sentence here is that the operator still has to
 * confirm it. Somebody who believes an upload settled their bill stops chasing
 * it, and finds out when they are cut off again.
 */
const peso = (n: number) => `₱${Number(n ?? 0).toLocaleString()}`;
const METHOD: Record<string, string> = {
   cash: 'Cash',
   gcash: 'GCash',
   maya: 'Maya',
   bank: 'Bank',
   other: 'Other',
};

/** '8:42 am' — a time a customer recognises, in their own timezone. */
const clock = (iso: string) =>
   new Date(iso).toLocaleString('en-PH', {
      day: 'numeric',
      month: 'short',
      hour: 'numeric',
      minute: '2-digit',
   });

const day = (iso: string) =>
   new Date(iso).toLocaleDateString('en-PH', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
   });

/** Session key, per business — one phone can be a customer of two operators. */
const tokenKey = (slug: string) => `isp-portal:${slug}`;

/** Whole days from now until `iso`. Negative once it is in the past. */
const daysUntil = (iso: string) =>
   Math.ceil(
      (new Date(iso).setHours(0, 0, 0, 0) - new Date().setHours(0, 0, 0, 0)) /
         86400000
   );

const IspPortal: React.FC = () => {
   const { slug = '' } = useParams();
   const qc = useQueryClient();

   const [token, setToken] = useState<string>(() => {
      try {
         return localStorage.getItem(tokenKey(slug)) ?? '';
      } catch {
         // Private browsing throws on read. Signing in again is a small cost;
         // a blank page is not.
         return '';
      }
   });

   // The operator's branding, BEFORE anybody signs in. Without it the first
   // screen is an unbranded form that could belong to anyone.
   const { data: pub } = useQuery({
      queryKey: ['isp-public', slug],
      queryFn: () => getIspPublicTenant(slug),
      retry: false,
   });

   /** Which bill is open, full size and printable. */
   const [openBill, setOpenBill] = useState<string | null>(null);
   const [showPay, setShowPay] = useState(false);

   const [phone, setPhone] = useState('');
   const [surname, setSurname] = useState('');
   const [needsSurname, setNeedsSurname] = useState(false);
   const [loginErr, setLoginErr] = useState('');

   const signIn = useMutation({
      mutationFn: () => startIspPortalSession(slug, phone, surname || undefined),
      onSuccess: (r) => {
         setToken(r.token);
         try {
            localStorage.setItem(tokenKey(slug), r.token);
         } catch {
            // Not fatal: the token lives in memory for this visit either way.
         }
      },
      onError: (e: unknown) => {
         const d = (
            e as { response?: { data?: { error?: string; needsSurname?: boolean } } }
         )?.response?.data;
         setNeedsSurname(d?.needsSurname === true);
         setLoginErr(d?.error ?? 'Could not sign you in.');
      },
   });

   const { data, isLoading, isError } = useQuery({
      queryKey: ['isp-portal-me', slug, token],
      queryFn: () => getIspPortalMe(token),
      enabled: !!token,
      retry: false,
   });

   /**
    * A token the server no longer accepts is worse than no token: every visit
    * spends a round trip being rejected before the sign-in card appears. Drop
    * it the first time it is refused.
    */
   useEffect(() => {
      if (!isError || !token) return;
      try {
         localStorage.removeItem(tokenKey(slug));
      } catch {
         /* nothing to clear */
      }
   }, [isError, token, slug]);

   const signOut = () => {
      try {
         localStorage.removeItem(tokenKey(slug));
      } catch {
         /* nothing to clear */
      }
      setToken('');
   };

   /* ------------------------------------------------------ the sign-in card */

   if (!token || isError) {
      const brand = pub
         ? {
              businessName: pub.businessName,
              logoUrl: pub.logoUrl,
              address: pub.address,
              contactNumber: pub.contactNumber,
           }
         : {
              businessName: 'Your provider',
              logoUrl: '',
              address: '',
              contactNumber: '',
           };
      return (
         <Shell>
            <PublicReset />
            <BrandHeader brand={brand} subtitle="Bills and payments" />
            <Body>
               <Card>
                  <CardTitle>Check your bill</CardTitle>
                  <CardNote>
                     Enter the mobile number {brand.businessName} has for you. No
                     password needed.
                  </CardNote>
                  <form
                     onSubmit={(e) => {
                        e.preventDefault();
                        setLoginErr('');
                        signIn.mutate();
                     }}
                  >
                     <Field>
                        <Lbl htmlFor="portal-phone">Mobile number</Lbl>
                        <Input
                           id="portal-phone"
                           value={phone}
                           inputMode="tel"
                           placeholder="0917 555 0142"
                           onChange={(e) => setPhone(e.target.value)}
                        />
                     </Field>
                     {/* Only asked when the number is genuinely shared. Asking
                         everybody for it would be a password by another name. */}
                     {needsSurname && (
                        <Field>
                           <Lbl htmlFor="portal-surname">Your last name</Lbl>
                           <Input
                              id="portal-surname"
                              value={surname}
                              onChange={(e) => setSurname(e.target.value)}
                           />
                        </Field>
                     )}
                     {loginErr ? <Err>{loginErr}</Err> : null}
                     <Primary type="submit" disabled={signIn.isPending}>
                        {signIn.isPending ? 'Checking…' : 'See my bill'}
                     </Primary>
                  </form>
               </Card>
            </Body>
            <BrandFooter brand={brand} />
         </Shell>
      );
   }

   if (isLoading || !data) return <PortalSkeleton brand={pub} />;

   const brand = {
      businessName: data.businessName,
      logoUrl: data.logoUrl,
      address: data.address ?? '',
      contactNumber: data.contactNumber,
   };
   const bill = data.invoices.find((i) => i.id === openBill) ?? null;
   const owes = data.outstanding > 0;
   const waiting = data.proofs.find((p) => p.status === 'pending');
   const lastRejected = data.proofs.find((p) => p.status === 'rejected');
   const suspended = data.accounts.some((a) => a.status === 'suspended');
   /**
    * They have just been switched back on.
    *
    * 🔴 RESUMING DROPS THEIR LIVE SESSION, because RouterOS cannot move one
    * between profiles — the customer's router has to reconnect. Most redial in
    * seconds; Ray's Comfast did not and needed a power cycle (2026-09-03).
    *
    * Somebody who has just paid and still has no internet rings their
    * operator, and both of them think SmartFi is broken. One sentence on the
    * page they are already looking at is cheaper than that call.
    *
    * ⚠️ Two hours, not for ever: this is about the minutes after an approval,
    * and a permanent "restart your router" note is noise that teaches people
    * to ignore the page.
    */
   const justRestored =
      !suspended &&
      data.proofs.some(
         (p) =>
            p.status === 'approved' &&
            p.decidedAt &&
            Date.now() - new Date(p.decidedAt).getTime() < 2 * 60 * 60 * 1000
      );

   const suspendReason =
      data.accounts.find((a) => a.status === 'suspended' && a.suspendReason)
         ?.suspendReason ?? '';

   /**
    * What the customer is actually in.
    *
    * ─── BUSINESS LANGUAGE, NOT PLUMBING ───
    * Ray, 2026-09-02: *"'your internet is switch off' this is not business
    * terms."* Right — a provider says the service is suspended, not that
    * somebody flicked a switch. The words on a bill are the operator's voice,
    * and this page is their shopfront.
    *
    * 🔴 SUSPENDED WITH NOTHING OWED IS A REAL STATE, and the first version had
    * nothing to say about it. Found on Ray's own account: one invoice, paid in
    * full, and the line suspended by hand. Showing a ₱0 balance to somebody
    * with no internet answers the wrong question — what they need is who to
    * ring.
    */
   const soonest = data.invoices
      .filter((i) => i.outstanding > 0)
      .map((i) => daysUntil(i.dueAt))
      .sort((a, b) => a - b)[0];

   type State = 'settled-suspended' | 'suspended' | 'overdue' | 'due' | 'clear';
   const state: State = suspended && !owes
      ? 'settled-suspended'
      : suspended
        ? 'suspended'
        : owes && soonest !== undefined && soonest < 0
          ? 'overdue'
          : owes
            ? 'due'
            : 'clear';

   const CHIP: Record<State, string> = {
      'settled-suspended': 'Service suspended',
      suspended: 'Service suspended',
      overdue: 'Payment overdue',
      due: 'Payment due',
      clear: 'Account in good standing',
   };
   const NOTE: Record<State, string> = {
      // 🔴 The operator's own words when they have them. Ray, 2026-09-02: a
      // cut-off customer being told their account is "fully settled" is true,
      // useless, and the reason this field exists.
      'settled-suspended':
         suspendReason ||
         `Your account is fully settled. Please contact ${data.businessName} to have your service restored.`,
      suspended:
         suspendReason ||
         'Settle the amount below and your service will be restored.',
      overdue:
         soonest !== undefined
            ? `${Math.abs(soonest)} day${Math.abs(soonest) === 1 ? '' : 's'} past the due date.`
            : 'Please settle this to avoid interruption.',
      due:
         soonest !== undefined && soonest >= 0
            ? soonest === 0
               ? 'Due today.'
               : `Due in ${soonest} day${soonest === 1 ? '' : 's'}.`
            : 'Due soon.',
      clear: 'Nothing to pay right now. Thank you.',
   };

   return (
      <Wallet>
         <PublicReset />

         {/* ─── A site header, not an app bar. Ray, 2026-09-02: *"this is a
             business portal… its like their mini website."* A customer landing
             here should be in no doubt whose business this is, the same way
             they would be on any company's own page — name, tagline, and a way
             to call them, before anything about money. */}
         <SiteBar>
            <SiteInner>
               <SiteBrand>
                  {data.logoUrl ? (
                     <SiteLogo src={data.logoUrl} alt="" />
                  ) : (
                     <SiteMark>
                        {data.businessName.trim().charAt(0).toUpperCase()}
                     </SiteMark>
                  )}
                  <div>
                     <SiteName>{data.businessName}</SiteName>
                     <SiteTag>Customer billing</SiteTag>
                  </div>
               </SiteBrand>
               {data.contactNumber ? (
                  <SiteCall
                     href={`tel:${data.contactNumber.replace(/\s/g, '')}`}
                  >
                     Call us
                  </SiteCall>
               ) : null}
            </SiteInner>
         </SiteBar>

         {/* ─── The hero. An e-wallet opens on your balance, so this does too.
             Ray, 2026-09-02: *"like a ewallet vibe design"* — one dark card
             carrying the operator's identity and the single number the
             customer came for, with nothing competing against it. */}
         <Hero>
            <HeroTop>
               <div>
                  <HeroBiz>{data.customer.fullName}</HeroBiz>
                  <HeroWho>Account {data.customer.customerCode}</HeroWho>
               </div>
               <Chip $state={state}>{CHIP[state]}</Chip>
            </HeroTop>

            <HeroLabel>{owes ? 'Amount due' : 'Balance'}</HeroLabel>
            <HeroAmount>{peso(data.outstanding)}</HeroAmount>
            <HeroNote>{NOTE[state]}</HeroNote>

            <HeroActions>
               {owes ? (
                  <HeroPrimary as="a" href="#pay">
                     Pay now
                  </HeroPrimary>
               ) : null}
               {data.contactNumber ? (
                  <HeroGhost
                     as="a"
                     href={`tel:${data.contactNumber.replace(/\s/g, '')}`}
                  >
                     Call us
                  </HeroGhost>
               ) : null}
            </HeroActions>
         </Hero>

         <Body>
            {/* 🔴 THIS HAS TO LOOK LIKE SOMETHING IS HAPPENING. Ray,
                2026-09-03: *"it looks like its normal"* — a grey box for the
                one state where the customer has paid, is still cut off, and is
                waiting on a person. It carries a live pulse, the amount, and
                who is holding it. */}
            {waiting ? (
               <Pending>
                  <PendingTop>
                     <Pulse aria-hidden="true" />
                     <PendingLabel>Waiting for confirmation</PendingLabel>
                  </PendingTop>
                  <PendingAmount>{peso(waiting.amount)}</PendingAmount>
                  <PendingBody>
                     {data.businessName} is checking the receipt you sent. Your
                     balance stays as it is until they confirm it — there is
                     nothing more for you to do.
                  </PendingBody>
               </Pending>
            ) : null}

            {justRestored ? (
               <Restored>
                  <RestoredTick viewBox="0 0 24 24" aria-hidden="true">
                     <path d="m4 12.5 5.5 5.5L20 7" />
                  </RestoredTick>
                  <div>
                     <RestoredTitle>Your service is back on</RestoredTitle>
                     {data.businessName} has confirmed your payment. If you are
                     still not online after a minute, switch your router off and
                     on again — it needs to reconnect.
                  </div>
               </Restored>
            ) : null}

            {!waiting && lastRejected ? (
               <Banner $bad>
                  <BannerTitle>Your last payment was not accepted</BannerTitle>
                  {lastRejected.decisionReason ||
                     `Please call ${data.contactNumber || 'your provider'}.`}
               </Banner>
            ) : null}

            {/* ─── Pay ─── */}
            {owes && data.paymentMethods.length > 0 && (
               <Section id="pay">
                  <SectionHead>
                     <SectionTitle>Pay {peso(data.outstanding)}</SectionTitle>
                     <SectionSub>Send to any one of these</SectionSub>
                  </SectionHead>
                  <Methods>
                     {data.paymentMethods.map((m) => (
                        <PayMethod key={m.id} method={m} />
                     ))}
                  </Methods>
               </Section>
            )}

            {owes && !waiting && (
               <Section>
                  <SectionHead>
                     <SectionTitle>Already paid?</SectionTitle>
                     <SectionSub>
                        Send your receipt and {data.businessName} will confirm it
                     </SectionSub>
                  </SectionHead>
                  <ProofForm
                     token={token}
                     outstanding={data.outstanding}
                     businessName={data.businessName}
                     onSent={() =>
                        qc.invalidateQueries({ queryKey: ['isp-portal-me'] })
                     }
                  />
               </Section>
            )}

            {/* ─── What they pay for, and whether it is working ─── */}
            {data.accounts.map((a) => (
               <Section key={a.id}>
                  <SectionHead>
                     <SectionTitle>
                        {a.planName || 'Your connection'}
                     </SectionTitle>
                     {a.speedLabel || a.planPrice ? (
                        <SectionSub>
                           {[a.speedLabel, a.planPrice ? `${peso(a.planPrice)}/month` : '']
                              .filter(Boolean)
                              .join(' · ')}
                        </SectionSub>
                     ) : null}
                  </SectionHead>

                  <Line>
                     <LineMain>
                        <LineTitle>Status</LineTitle>
                        <LineSub>
                           {/* "Connected since" beats "online": it answers the
                               question somebody rings up about — has it been
                               dropping? */}
                           {a.status === 'active' && a.onlineSince
                              ? `Connected since ${clock(a.onlineSince)}`
                              : a.status === 'suspended'
                                ? 'Cut off until settled'
                                : 'Not connected'}
                        </LineSub>
                     </LineMain>
                     <Tag $bad={a.status !== 'active'}>
                        {a.status === 'active'
                           ? 'Active'
                           : a.status === 'suspended'
                             ? 'Suspended'
                             : a.status}
                     </Tag>
                  </Line>

                  <Line>
                     <LineMain>
                        <LineTitle>Login</LineTitle>
                        <LineSub>{a.name}</LineSub>
                     </LineMain>
                  </Line>

                  <Line>
                     <LineMain>
                        <LineTitle>Bills on</LineTitle>
                        <LineSub>Day {a.dueDay} of each month</LineSub>
                     </LineMain>
                  </Line>
               </Section>
            ))}

            {/* ─── Statements ─── */}
            {data.invoices.length > 0 && (
               <Section>
                  <SectionHead>
                     <SectionTitle>Statements</SectionTitle>
                     <SectionSub>Tap any to view or print</SectionSub>
                  </SectionHead>
                  {data.invoices.map((i) => (
                     <LineBtn
                        key={i.id}
                        type="button"
                        onClick={() => setOpenBill(i.id)}
                     >
                        <LineMain>
                           <LineTitle>{i.number}</LineTitle>
                           <LineSub>
                              {day(i.periodStart)} – {day(i.periodEnd)}
                           </LineSub>
                        </LineMain>
                        <LineRight>
                           {i.outstanding > 0 ? (
                              <Owing>{peso(i.outstanding)}</Owing>
                           ) : (
                              <Settled>Paid</Settled>
                           )}
                           {i.paidAmount > 0 && i.outstanding > 0 ? (
                              <LineSub>{peso(i.paidAmount)} paid</LineSub>
                           ) : null}
                        </LineRight>
                        <Chevron>›</Chevron>
                     </LineBtn>
                  ))}
               </Section>
            )}

            {/* ─── What they have paid ─── */}
            {data.payments.length > 0 && (
               <Section>
                  <SectionHead>
                     <SectionTitle>Payments received</SectionTitle>
                     <SectionSub>
                        Confirmed by {data.businessName}
                     </SectionSub>
                  </SectionHead>
                  {data.payments.map((p) => (
                     <Line key={p.id}>
                        <LineMain>
                           <LineTitle>{peso(p.amount)}</LineTitle>
                           <LineSub>
                              {day(p.receivedAt)}
                              {p.reference ? ` · ${p.reference}` : ''}
                           </LineSub>
                        </LineMain>
                        <Tag>{METHOD[p.method] ?? p.method}</Tag>
                     </Line>
                  ))}
               </Section>
            )}

            {/* Reachable when nothing is owed, closed by default — somebody
                paying early should not have to wait to be billed, but an open
                QR invites a payment nobody owes. */}
            {!owes && data.paymentMethods.length > 0 && (
               <Section>
                  <Reveal
                     type="button"
                     onClick={() => setShowPay((v) => !v)}
                     aria-expanded={showPay}
                  >
                     {showPay ? 'Hide payment details' : 'Pay in advance'}
                  </Reveal>
                  {showPay ? (
                     <Methods>
                        {data.paymentMethods.map((m) => (
                           <PayMethod key={m.id} method={m} />
                        ))}
                     </Methods>
                  ) : null}
               </Section>
            )}

            {/* A real footer: who they are, where they are, how to reach
                them. On a bill this is not decoration — it is the answer to
                "who do I call", and the customer reading it often has no
                internet beyond this page. */}
            <SiteFoot>
               <FootBiz>{data.businessName}</FootBiz>
               {brand.address ? <FootLine>{brand.address}</FootLine> : null}
               {data.contactNumber ? (
                  <FootLine>
                     <FootLink
                        href={`tel:${data.contactNumber.replace(/\s/g, '')}`}
                     >
                        {data.contactNumber}
                     </FootLink>
                  </FootLine>
               ) : null}
               <FootRule />
               <SignOut type="button" onClick={signOut}>
                  Sign out
               </SignOut>
               <Powered>
                  <PoweredDot aria-hidden="true" />
                  Billing by <strong>SmartFi</strong>
               </Powered>
            </SiteFoot>
         </Body>

         {bill ? (
            <IspBillSheet
               brand={brand}
               customer={data.customer}
               invoice={bill as never}
               methods={data.paymentMethods}
               token={token}
               onClose={() => setOpenBill(null)}
            />
         ) : null}
      </Wallet>
   );
};

/**
 * "I have paid" — a claim, and it says so.
 *
 * The amount defaults to what they owe, because that is what most people send,
 * but it is editable: somebody paying half must be able to say so rather than
 * lying about the figure to make the form accept it.
 */
const ProofForm: React.FC<{
   token: string;
   outstanding: number;
   businessName: string;
   onSent: () => void;
}> = ({ token, outstanding, businessName, onSent }) => {
   const [amount, setAmount] = useState(String(outstanding));
   const [reference, setReference] = useState('');
   const [proofUrl, setProofUrl] = useState('');
   const [pickerBlocked, setPickerBlocked] = useState(false);
   const [sent, setSent] = useState(false);
   const [err, setErr] = useState('');

   const send = useMutation({
      mutationFn: () =>
         submitIspPortalProof(token, {
            amount: Number(amount),
            proofUrl,
            reference,
         }),
      onSuccess: () => {
         setSent(true);
         onSent();
      },
      onError: (e: unknown) =>
         setErr(
            (e as { response?: { data?: { error?: string } } })?.response?.data
               ?.error ?? 'Could not send that.'
         ),
   });

   /**
    * 🔴 SAY IT LANDED. Ray, 2026-09-03: *"after submit it doesnt show
    * success."* The form simply emptied and the page refreshed underneath —
    * which, to somebody who has just sent money and is cut off, reads exactly
    * like nothing happened. So the form is replaced by an answer.
    */
   if (sent) {
      return (
         <SentCard>
            <SentTick viewBox="0 0 24 24" aria-hidden="true">
               <path d="m4 12.5 5.5 5.5L20 7" />
            </SentTick>
            <SentTitle>Receipt sent</SentTitle>
            <SentBody>
               {businessName} will check it and switch your service back on once
               they confirm. You do not need to send it again.
            </SentBody>
         </SentCard>
      );
   }

   return (
      <>
         <Field>
            <Lbl htmlFor="proof-amount">How much did you send?</Lbl>
            {/* type=text with inputMode, never type=number: a number input eats
                the decimal point on some Android keyboards. */}
            <Input
               id="proof-amount"
               type="text"
               inputMode="decimal"
               value={amount}
               onChange={(e) => setAmount(e.target.value)}
            />
         </Field>
         <Field>
            <Lbl htmlFor="proof-ref">Reference number (optional)</Lbl>
            <Input
               id="proof-ref"
               value={reference}
               onChange={(e) => setReference(e.target.value)}
            />
            <Hint>The number your wallet showed after sending.</Hint>
         </Field>
         <Field>
            <Lbl>Screenshot of your payment</Lbl>
            <IspProofUpload
               token={token}
               value={proofUrl}
               onChange={setProofUrl}
               onPickerBlocked={() => setPickerBlocked(true)}
            />
            {/* 🔴 ONLY once the picker has actually refused to open. Ray,
                2026-09-03: it must "only show if the browser is captive mini".
                Shown on every phone it would be noise, and guessed from the
                user agent it would misfire on Messenger, where uploads work
                fine — so it waits for the failure itself. */}
            {pickerBlocked ? <IspPortalDiag /> : null}
         </Field>
         {err ? <Err>{err}</Err> : null}
         <Primary
            type="button"
            disabled={send.isPending || !proofUrl || Number(amount) <= 0}
            onClick={() => {
               setErr('');
               send.mutate();
            }}
         >
            {send.isPending ? 'Sending…' : 'Send proof of payment'}
         </Primary>
         {/* 🔴 The sentence that stops somebody believing they are settled. */}
         <Fine>
            This does not pay the bill by itself — {businessName} checks it
            first, and your balance changes when they confirm.
         </Fine>
      </>
   );
};

/**
 * An e-wallet, not a form.
 *
 * Ray said it more than once and I kept shipping a stack of white boxes:
 * *"so ugly, boring… it should look professional, compact, modern, like a
 * ewallet vibe."* The rebuild follows the one thing every wallet app does — it
 * opens on your balance, in a dark card, with the operator's name on it and
 * nothing competing for attention. Everything else is a quiet list underneath.
 */
const TONE: Record<string, string> = {
   'settled-suspended': '#f59e0b',
   suspended: '#ef4444',
   overdue: '#ef4444',
   due: '#f59e0b',
   clear: '#22c55e',
};

/**
 * What the customer looks at while their bill loads.
 *
 * Ray, 2026-09-03: *"i see a plain simple basic loading. please enhance that
 * to spinner or skeleton loading."* It said "Loading…" on an empty white page,
 * which reads as broken on a slow phone — and a spinner would say no more.
 *
 * 🔴 IT MIRRORS THE REAL PAGE, and that is the whole point: same header, same
 * hero, same section cards in the same places, so the layout does not jump
 * when the data lands. A skeleton of the wrong shape is worse than none.
 *
 * And the branding is REAL, not grey. The provider's name and logo come from
 * the public lookup, which has usually answered by now — so the customer knows
 * whose page they are on before the money arrives.
 */
const PortalSkeleton: React.FC<{
   brand?: { businessName: string; logoUrl: string } | null;
}> = ({ brand }) => (
   <Wallet>
      <PublicReset />
      <SiteBar>
         <SiteInner>
            <SiteBrand>
               {brand?.logoUrl ? (
                  <SiteLogo src={brand.logoUrl} alt="" />
               ) : brand?.businessName ? (
                  <SiteMark>
                     {brand.businessName.trim().charAt(0).toUpperCase()}
                  </SiteMark>
               ) : (
                  <Bone $w="38px" $h="38px" $r="11px" />
               )}
               <div>
                  {brand?.businessName ? (
                     <SiteName>{brand.businessName}</SiteName>
                  ) : (
                     <Bone $w="150px" $h="15px" />
                  )}
                  <SiteTag>Customer billing</SiteTag>
               </div>
            </SiteBrand>
         </SiteInner>
      </SiteBar>

      <Hero>
         <HeroLabel>Amount due</HeroLabel>
         {/* The number they came for. Wide and tall, because a short bar here
             makes the real figure shove everything down when it arrives. */}
         <Bone $w="190px" $h="40px" $mt="6px" $mx="auto" $dark />
         <Bone $w="230px" $h="13px" $mt="14px" $mx="auto" $dark />
         <HeroActions>
            <Bone $w="100%" $h="42px" $r="12px" $dark />
            <Bone $w="100%" $h="42px" $r="12px" $dark />
         </HeroActions>
      </Hero>

      <Body>
         {[0, 1].map((i) => (
            <Section key={i} aria-hidden="true">
               <Bone $w="120px" $h="14px" />
               <Bone $w="100%" $h="12px" $mt="14px" />
               <Bone $w="80%" $h="12px" $mt="8px" />
               <Bone $w="60%" $h="12px" $mt="8px" />
            </Section>
         ))}
      </Body>

      <SrOnly role="status" aria-live="polite">
         Loading your bill
      </SrOnly>
   </Wallet>
);

const shimmer = keyframes`
   from { background-position: -420px 0; }
   to { background-position: 420px 0; }
`;

/** One grey bar. `$dark` for the ones sitting on the hero. */
const Bone = styled.div<{
   $w?: string;
   $h?: string;
   $r?: string;
   $mt?: string;
   $mx?: string;
   $dark?: boolean;
}>`
   width: ${({ $w }) => $w ?? '100%'};
   height: ${({ $h }) => $h ?? '12px'};
   border-radius: ${({ $r }) => $r ?? '6px'};
   margin-top: ${({ $mt }) => $mt ?? '0'};
   margin-left: ${({ $mx }) => $mx ?? '0'};
   margin-right: ${({ $mx }) => $mx ?? '0'};
   background: ${({ $dark }) =>
         $dark ? 'rgba(255, 255, 255, 0.16)' : colors.GRAY13}
      linear-gradient(
         90deg,
         transparent 0%,
         ${({ $dark }) =>
            $dark ? 'rgba(255,255,255,0.28)' : 'rgba(255,255,255,0.85)'} 50%,
         transparent 100%
      );
   background-size: 420px 100%;
   background-repeat: no-repeat;
   animation: ${shimmer} 1.25s ease-in-out infinite;

   /* Somebody who asked for less motion gets a plain grey bar. */
   @media (prefers-reduced-motion: reduce) {
      animation: none;
   }
`;

const SrOnly = styled.div`
   position: absolute;
   width: 1px;
   height: 1px;
   overflow: hidden;
   clip: rect(0 0 0 0);
   white-space: nowrap;
`;

const Wallet = styled.div`
   min-height: 100vh;
   background: ${colors.GRAY14};
`;
/**
 * The operator's own header. Ray, 2026-09-02: *"this is a business portal…
 * its like their mini website."* Sticky, so their name stays on screen while
 * a customer scrolls their statements — the way a company's own site behaves,
 * not the way a form does.
 */
const SiteBar = styled.header`
   position: sticky;
   top: 0;
   z-index: 5;
   background: ${colors.WHITE};
   border-bottom: 1px solid ${colors.GRAY12};
`;
const SiteInner = styled.div`
   max-width: 560px;
   margin: 0 auto;
   padding: 12px 18px;
   display: flex;
   align-items: center;
   justify-content: space-between;
   gap: 12px;
`;
const SiteBrand = styled.div`
   display: flex;
   align-items: center;
   gap: 11px;
   min-width: 0;
`;
const SiteLogo = styled.img`
   width: 36px;
   height: 36px;
   border-radius: 10px;
   object-fit: cover;
   flex: none;
`;
const SiteMark = styled.div`
   width: 36px;
   height: 36px;
   border-radius: 10px;
   flex: none;
   background: ${colors.PRIMARY};
   color: #fff;
   display: grid;
   place-items: center;
   font-size: 16px;
   font-weight: 800;
`;
const SiteName = styled.div`
   font-size: 15px;
   font-weight: 800;
   line-height: 1.2;
`;
const SiteTag = styled.div`
   font-size: 11px;
   text-transform: uppercase;
   letter-spacing: 0.6px;
   color: ${colors.GRAY5};
`;
const SiteCall = styled.a`
   flex: none;
   font-size: 13px;
   font-weight: 800;
   padding: 8px 14px;
   border-radius: 999px;
   border: 1px solid ${colors.GRAY12};
   color: ${colors.PRIMARY};
   text-decoration: none;
`;
const SiteFoot = styled.footer`
   text-align: center;
   padding: 18px 0 4px;
`;
const FootBiz = styled.div`
   font-size: 14px;
   font-weight: 800;
`;
const FootRule = styled.hr`
   border: none;
   border-top: 1px solid ${colors.GRAY12};
   margin: 16px auto 12px;
   max-width: 180px;
`;

const Hero = styled.div`
   background: linear-gradient(160deg, #1e2a5a 0%, ${colors.PRIMARY} 100%);
   color: #fff;
   padding: 22px 20px 30px;
   border-radius: 0 0 26px 26px;
`;
const HeroTop = styled.div`
   max-width: 520px;
   margin: 0 auto 20px;
   display: flex;
   align-items: flex-start;
   justify-content: space-between;
   gap: 12px;
`;
const HeroBrand = styled.div`
   display: flex;
   align-items: center;
   gap: 11px;
   min-width: 0;
`;
const HeroLogo = styled.img`
   width: 40px;
   height: 40px;
   border-radius: 11px;
   object-fit: cover;
   background: #fff;
   flex: none;
`;
const HeroMark = styled.div`
   width: 40px;
   height: 40px;
   border-radius: 11px;
   flex: none;
   background: rgba(255, 255, 255, 0.18);
   display: grid;
   place-items: center;
   font-size: 18px;
   font-weight: 800;
`;
const HeroBiz = styled.div`
   font-size: 15px;
   font-weight: 800;
   line-height: 1.2;
`;
const HeroWho = styled.div`
   font-size: 12px;
   opacity: 0.8;
`;
const Chip = styled.span<{ $state: string }>`
   flex: none;
   font-size: 11px;
   font-weight: 800;
   padding: 5px 11px;
   border-radius: 999px;
   white-space: nowrap;
   color: ${({ $state }) => TONE[$state] ?? '#fff'};
   background: #fff;
`;
const HeroLabel = styled.div`
   max-width: 520px;
   margin: 0 auto;
   font-size: 12px;
   text-transform: uppercase;
   letter-spacing: 0.6px;
   opacity: 0.75;
`;
const HeroAmount = styled.div`
   max-width: 520px;
   margin: 2px auto 0;
   font-size: 42px;
   font-weight: 800;
   line-height: 1.1;
   letter-spacing: -0.5px;
`;
const HeroNote = styled.div`
   max-width: 520px;
   margin: 8px auto 0;
   font-size: 13px;
   line-height: 1.55;
   opacity: 0.9;
`;
const HeroActions = styled.div`
   max-width: 520px;
   margin: 18px auto 0;
   display: flex;
   align-items: stretch;
   gap: 9px;
`;
/**
 * 🔴 THESE MUST NEVER WRAP.
 *
 * The second button used to read "Call {businessName}". A name of any length —
 * "Rayjens Code's ISP" is enough — broke onto a second line, so that button
 * grew taller and "Pay now" beside it stretched with a band of dead space
 * underneath (Ray, 2026-09-02). The label is now fixed-length, the text cannot
 * break, and a long label is trimmed rather than allowed to reflow. The
 * business name is already in the header directly above.
 */
const heroBtn = `
   flex: 1 1 0;
   min-width: 0;
   text-align: center;
   font-family: inherit;
   font-size: 14px;
   font-weight: 800;
   line-height: 1.2;
   padding: 12px 14px;
   border-radius: 12px;
   text-decoration: none;
   cursor: pointer;
   white-space: nowrap;
   overflow: hidden;
   text-overflow: ellipsis;
`;
const HeroPrimary = styled.button`
   ${heroBtn}
   border: none;
   background: #fff;
   color: ${colors.PRIMARY};
`;
const HeroGhost = styled.button`
   ${heroBtn}
   border: 1px solid rgba(255, 255, 255, 0.45);
   background: transparent;
   color: #fff;
`;
const Body = styled.div`
   max-width: 520px;
   margin: 0 auto;
   padding: 18px 16px 28px;
`;
const Banner = styled.div<{ $bad?: boolean }>`
   background: ${colors.WHITE};
   border: 1px solid ${({ $bad }) => ($bad ? '#ef4444' : colors.GRAY12)};
   border-left: 4px solid ${({ $bad }) => ($bad ? '#ef4444' : '#f59e0b')};
   border-radius: 14px;
   padding: 14px 16px;
   margin-bottom: 14px;
   font-size: 13px;
   line-height: 1.6;
   color: ${colors.GRAY5};
`;
const BannerTitle = styled.div`
   font-size: 14px;
   font-weight: 800;
   color: inherit;
   margin-bottom: 3px;
`;
const Section = styled.section`
   background: ${colors.WHITE};
   border: 1px solid ${colors.GRAY12};
   border-radius: 16px;
   padding: 16px;
   margin-bottom: 14px;
`;
const SectionHead = styled.div`
   margin-bottom: 12px;
`;
const SectionTitle = styled.h2`
   margin: 0;
   font-size: 15px;
   font-weight: 800;
`;
const SectionSub = styled.div`
   margin-top: 2px;
   font-size: 12px;
   color: ${colors.GRAY5};
`;
const Methods = styled.div``;
const rowBase = `
   width: 100%;
   display: flex;
   align-items: center;
   gap: 12px;
   padding: 12px 0;
   text-align: left;
`;
const Line = styled.div`
   ${rowBase}
   & + & {
      border-top: 1px solid ${colors.GRAY12};
   }
`;
const LineBtn = styled.button`
   ${rowBase}
   font-family: inherit;
   color: inherit;
   background: none;
   border: none;
   cursor: pointer;
   & + & {
      border-top: 1px solid ${colors.GRAY12};
   }
`;
const LineMain = styled.div`
   flex: 1;
   min-width: 0;
`;
const LineTitle = styled.div`
   font-size: 14px;
   font-weight: 700;
`;
const LineSub = styled.div`
   font-size: 12px;
   color: ${colors.GRAY5};
`;
const LineRight = styled.div`
   text-align: right;
`;
const Owing = styled.div`
   font-size: 15px;
   font-weight: 800;
   color: #ef4444;
`;
const Settled = styled.div`
   font-size: 13px;
   font-weight: 800;
   color: #22c55e;
`;
const Chevron = styled.span`
   font-size: 20px;
   color: ${colors.GRAY5};
   line-height: 1;
`;
const Tag = styled.span<{ $bad?: boolean }>`
   font-size: 11px;
   font-weight: 800;
   padding: 5px 11px;
   border-radius: 999px;
   color: ${({ $bad }) => ($bad ? '#ef4444' : '#22c55e')};
   background: ${({ $bad }) =>
      $bad ? 'rgba(239,68,68,0.1)' : 'rgba(34,197,94,0.12)'};
`;
const Reveal = styled.button`
   width: 100%;
   font-family: inherit;
   font-size: 14px;
   font-weight: 800;
   padding: 12px;
   border: 1px dashed ${colors.GRAY12};
   border-radius: 12px;
   background: transparent;
   color: ${colors.PRIMARY};
   cursor: pointer;
`;
const Foot = styled.div`
   text-align: center;
   padding: 8px 0 4px;
`;
const FootLine = styled.div`
   font-size: 13px;
   color: ${colors.GRAY5};
`;
const FootLink = styled.a`
   color: ${colors.PRIMARY};
   font-weight: 700;
   text-decoration: none;
`;
const SignOut = styled.button`
   margin-top: 10px;
   font-family: inherit;
   font-size: 13px;
   border: none;
   background: none;
   color: ${colors.GRAY5};
   cursor: pointer;
   text-decoration: underline;
`;
/** A small badge rather than a grey caption. Ray asked for "a pill something". */
const Powered = styled.div`
   display: inline-flex;
   align-items: center;
   gap: 6px;
   margin-top: 16px;
   padding: 6px 13px 6px 10px;
   border: 1px solid ${colors.GRAY12};
   border-radius: 99px;
   background: ${colors.WHITE};
   font-size: 11px;
   letter-spacing: 0.02em;
   color: ${colors.GRAY5};

   strong {
      font-weight: 800;
      color: ${colors.MAIN_DARK};
   }
`;
const PoweredDot = styled.span`
   width: 6px;
   height: 6px;
   border-radius: 50%;
   background: ${colors.PRIMARY};
`;

const pulse = keyframes`
   0%   { transform: scale(1);   opacity: 1; }
   70%  { transform: scale(2.6); opacity: 0; }
   100% { transform: scale(2.6); opacity: 0; }
`;
const Pending = styled.div`
   position: relative;
   overflow: hidden;
   background: linear-gradient(180deg, #fffdf5 0%, #fffbeb 100%);
   border: 1px solid #fcd34d;
   border-left: 5px solid #f59e0b;
   border-radius: 14px;
   padding: 14px 16px;
   margin-bottom: 14px;
`;
const PendingTop = styled.div`
   display: flex;
   align-items: center;
   gap: 9px;
`;
const Pulse = styled.span`
   position: relative;
   width: 9px;
   height: 9px;
   border-radius: 50%;
   background: #f59e0b;
   flex: none;

   &::after {
      content: '';
      position: absolute;
      inset: 0;
      border-radius: 50%;
      background: #f59e0b;
      animation: ${pulse} 1.8s ease-out infinite;
   }

   @media (prefers-reduced-motion: reduce) {
      &::after { animation: none; }
   }
`;
const PendingLabel = styled.div`
   font-size: 11px;
   font-weight: 800;
   letter-spacing: 0.08em;
   text-transform: uppercase;
   color: #b45309;
`;
const PendingAmount = styled.div`
   margin: 6px 0 2px;
   font-size: 26px;
   font-weight: 800;
   letter-spacing: -0.02em;
   color: #92400e;
`;
const PendingBody = styled.p`
   margin: 0;
   font-size: 13px;
   line-height: 1.55;
   color: #92400e;
`;

const Restored = styled.div`
   display: flex;
   gap: 10px;
   align-items: flex-start;
   background: linear-gradient(180deg, #f6fffb 0%, #ecfdf5 100%);
   border: 1px solid #a7f3d0;
   border-left: 5px solid #059669;
   border-radius: 14px;
   padding: 14px 16px;
   margin-bottom: 14px;
   font-size: 13px;
   line-height: 1.55;
   color: #047857;
`;
const RestoredTick = styled.svg`
   width: 20px;
   height: 20px;
   flex: none;
   margin-top: 1px;
   fill: none;
   stroke: #059669;
   stroke-width: 2.6;
   stroke-linecap: round;
   stroke-linejoin: round;
`;
const RestoredTitle = styled.div`
   font-size: 14px;
   font-weight: 800;
   color: #065f46;
   margin-bottom: 2px;
`;
const SentCard = styled.div`
   text-align: center;
   padding: 22px 16px;
   border: 1px solid #a7f3d0;
   border-radius: 14px;
   background: linear-gradient(180deg, #f6fffb 0%, #ecfdf5 100%);
`;
const SentTick = styled.svg`
   width: 34px;
   height: 34px;
   fill: none;
   stroke: #059669;
   stroke-width: 2.4;
   stroke-linecap: round;
   stroke-linejoin: round;
`;
const SentTitle = styled.div`
   margin-top: 8px;
   font-size: 17px;
   font-weight: 800;
   color: #065f46;
`;
const SentBody = styled.p`
   margin: 6px auto 0;
   max-width: 330px;
   font-size: 13px;
   line-height: 1.55;
   color: #047857;
`;

export default IspPortal;
